import mqtt, { type MqttClient, type IClientOptions } from "mqtt";
// global state
import { globalState } from "../framework/globalState.ts";
import type { MqttMessageCallback } from '../types/index.ts';

let client: MqttClient | null = null;
const subscriptions: Record<string, MqttMessageCallback[]> = {};
const MQTT_TOGGLE_KEY = "vetra.mqttLiveEnabled";
const MQTT_DEBUG_LOG_KEY = "vetra.mqttDebugLog";
const MQTT_CONNECT_GRACE_MS = 6000;
let lastMqttInitAttemptAt = 0;

function isMqttDebugLogEnabled(): boolean {
  // Default ON to help field debugging unless explicitly disabled.
  return localStorage.getItem(MQTT_DEBUG_LOG_KEY) !== "false";
}

function logMqttTraffic(label: string, topic: string, payload?: unknown): void {
  if (!isMqttDebugLogEnabled()) return;
  if (payload === undefined) {
    console.log(`[MQTT:${label}] topic=${topic}`);
    return;
  }
  console.log(`[MQTT:${label}] topic=${topic}`, payload);
}

type PollSiteEntry = {
  registerCallbacks: Map<string, Set<MqttMessageCallback>>;
  stateCallbacks: Set<MqttMessageCallback>;
  intervalId: ReturnType<typeof setInterval> | null;
  inFlight: boolean;
};

const pollSiteMap = new Map<string, PollSiteEntry>();

function isMqttLiveEnabled(): boolean {
  return localStorage.getItem(MQTT_TOGGLE_KEY) !== "false";
}

export function isMqttRealtimeEnabled(): boolean {
  return isMqttLiveEnabled();
}

export function isMqttConnected(): boolean {
  return Boolean(client?.connected);
}

function isLocalhostRuntime(): boolean {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

function resolveRuntimeVar(key: 'VITE_MQTT_URL' | 'VITE_MQTT_USER' | 'VITE_MQTT_PASS'): string {
  const fromRuntime = String(window.__ENV__?.[key] || '');
  const fromVite = String(import.meta.env[key] || '');

  if (isLocalhostRuntime()) {
    return fromVite || fromRuntime || '';
  }

  return fromRuntime || fromVite || '';
}

function getApiBase(): string {
  const rawBase = ((window.__ENV__?.VITE_API_URL)
    || import.meta.env.VITE_API_URL
    || "https://api.lapomikenergy.com").replace(/\/+$/, '');

  return /\/v\d+$/i.test(rawBase) ? rawBase : `${rawBase}/v1`;
}

async function pollSiteRegisters(siteId: string, entry: PollSiteEntry): Promise<void> {
  if (isMqttLiveEnabled() && client?.connected) return;
  if (isMqttLiveEnabled() && !client?.connected && lastMqttInitAttemptAt > 0) {
    const elapsed = Date.now() - lastMqttInitAttemptAt;
    if (elapsed < MQTT_CONNECT_GRACE_MS) return;
  }
  if (entry.inFlight) return;
  if (!entry.registerCallbacks.size && !entry.stateCallbacks.size) return;

  entry.inFlight = true;
  try {
    const token = globalState.token as string | null;
    if (!token) return;

    const res = await fetch(`${getApiBase()}/sites/${siteId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    if (!res.ok) return;

    const body = await res.json();
    const data = body?.success ? body.data : body;
    const devices = Array.isArray(data?.devices) ? data.devices : [];

    // If MQTT reconnected while this poll request was in-flight, do not
    // dispatch API values to subscribers (prevents API overriding live MQTT).
    if (isMqttLiveEnabled() && client?.connected) {
      return;
    }

    devices.forEach((device: any) => {
      if (!entry.stateCallbacks.size) return;

      const registers = Array.isArray(device?.registers) ? device.registers : [];
      const readings = registers.map((reg: any) => ({
        registerId: String(reg?._id || ''),
        deviceId: String(device?._id || ''),
        name: reg?.name,
        address: reg?.address,
        value: reg?.value ?? null,
        unit: reg?.unit ?? null,
        timestamp: reg?.updatedAt || new Date().toISOString(),
        quality: reg?.quality || 'GOOD',
        deviceStatus: device?.status || 'unknown',
      }));

      const payload = {
        siteId,
        deviceId: String(device?._id || ''),
        source: 'api-poll',
        timestamp: new Date().toISOString(),
        readingCount: readings.length,
        readings,
      };

      entry.stateCallbacks.forEach((cb) => {
        try { cb(payload); } catch (err) { console.warn(err); }
      });
    });

    devices.forEach((device: any) => {
      const registers = Array.isArray(device?.registers) ? device.registers : [];
      registers.forEach((reg: any) => {
        const regId = String(reg?._id || "");
        if (!regId || !entry.registerCallbacks.has(regId)) return;

        const callbacks = entry.registerCallbacks.get(regId);
        if (!callbacks?.size) return;

        const payload = {
          value: reg?.value ?? null,
          registerId: regId,
          deviceId: device?._id,
          source: 'api-poll',
          ts: reg?.updatedAt || new Date().toISOString(),
        };

        callbacks.forEach((cb) => {
          try { cb(payload); } catch (err) { console.warn(err); }
        });
      });
    });
  } catch (err) {
    console.warn(`[Polling] Failed site poll (${siteId})`, err);
  } finally {
    entry.inFlight = false;
  }
}

function ensureSitePolling(siteId: string): PollSiteEntry {
  let entry = pollSiteMap.get(siteId);
  if (!entry) {
    entry = {
      registerCallbacks: new Map<string, Set<MqttMessageCallback>>(),
      stateCallbacks: new Set<MqttMessageCallback>(),
      intervalId: null,
      inFlight: false,
    };
    pollSiteMap.set(siteId, entry);
  }

  if (!entry.intervalId) {
    entry.intervalId = setInterval(() => {
      void pollSiteRegisters(siteId, entry!);
    }, 10000);
  }

  return entry;
}

function addPollingRegisterSubscription(siteId: string, registerId: string, callback: MqttMessageCallback): () => void {
  const entry = ensureSitePolling(siteId);
  const regKey = String(registerId);

  if (!entry.registerCallbacks.has(regKey)) {
    entry.registerCallbacks.set(regKey, new Set());
  }
  entry.registerCallbacks.get(regKey)!.add(callback);

  // Prime immediately when API mode is active
  if (isMqttLiveEnabled() && !client?.connected && lastMqttInitAttemptAt > 0) {
    const remaining = Math.max(0, MQTT_CONNECT_GRACE_MS - (Date.now() - lastMqttInitAttemptAt));
    window.setTimeout(() => void pollSiteRegisters(siteId, entry), remaining + 50);
  } else {
    void pollSiteRegisters(siteId, entry);
  }

  return () => {
    const callbacks = entry.registerCallbacks.get(regKey);
    if (callbacks) {
      callbacks.delete(callback);
      if (!callbacks.size) entry.registerCallbacks.delete(regKey);
    }

    if (!entry.registerCallbacks.size && !entry.stateCallbacks.size) {
      if (entry.intervalId) clearInterval(entry.intervalId);
      entry.intervalId = null;
      pollSiteMap.delete(siteId);
    }
  };
}

function addPollingStateSubscription(siteId: string, callback: MqttMessageCallback): () => void {
  const entry = ensureSitePolling(siteId);
  entry.stateCallbacks.add(callback);

  // Prime immediately when API mode is active
  if (isMqttLiveEnabled() && !client?.connected && lastMqttInitAttemptAt > 0) {
    const remaining = Math.max(0, MQTT_CONNECT_GRACE_MS - (Date.now() - lastMqttInitAttemptAt));
    window.setTimeout(() => void pollSiteRegisters(siteId, entry), remaining + 50);
  } else {
    void pollSiteRegisters(siteId, entry);
  }

  return () => {
    entry.stateCallbacks.delete(callback);

    if (!entry.registerCallbacks.size && !entry.stateCallbacks.size) {
      if (entry.intervalId) clearInterval(entry.intervalId);
      entry.intervalId = null;
      pollSiteMap.delete(siteId);
    }
  };
}

function topicMatches(subscriptionTopic: string, actualTopic: string): boolean {
  const subParts = subscriptionTopic.split('/');
  const actualParts = actualTopic.split('/');

  let actualIndex = 0;
  for (let subIndex = 0; subIndex < subParts.length; subIndex += 1) {
    const subPart = subParts[subIndex];
    if (subPart === '#') return true;
    const actualPart = actualParts[actualIndex];
    if (actualPart === undefined) return false;
    if (subPart !== '+' && subPart !== actualPart) return false;
    actualIndex += 1;
  }

  return actualIndex === actualParts.length;
}

// ----------------- Initialize MQTT -----------------
export function initMqtt(token: string): Promise<MqttClient> {
  if (!token) {
    console.error("❌ Missing MQTT token");
    return Promise.reject(new Error("Missing MQTT token"));
  }

  return new Promise((resolve, reject) => {
    if (client?.connected) return resolve(client);
    lastMqttInitAttemptAt = Date.now();

    // On localhost prefer Vite env (.env). In deployed runtime prefer window.__ENV__ injected by Docker.
    const mqttUrl: string = resolveRuntimeVar('VITE_MQTT_URL');
    const mqttPass: string = resolveRuntimeVar('VITE_MQTT_PASS');
    const mqttUser: string = resolveRuntimeVar('VITE_MQTT_USER') || "frontend";

    if (!mqttUrl) {
      console.error("❌ MQTT URL is empty! Check environment variables.");
      reject(new Error("MQTT URL not configured"));
      return;
    }

    if (!/^wss?:\/\//i.test(mqttUrl)) {
      const message = `MQTT URL must be ws:// or wss:// for browser clients. Received: ${mqttUrl}`;
      console.error(`❌ ${message}`);
      reject(new Error(message));
      return;
    }

    console.log(`🔌 Connecting to MQTT: ${mqttUrl.replace(/\/\/.*@/, "//***@")}`);

    client = mqtt.connect(mqttUrl, {
        clientId: "vetra-frontend-" + Math.random().toString(16).slice(2),
      username: mqttUser,
        password: mqttPass,
        clean: true,
        reconnectPeriod: 3000,
        connectTimeout: 30000,
    } as IClientOptions);

    // ⏱️ Timeout: reject after 10 seconds if no connection
    const timeout = setTimeout(() => {
      if (!client?.connected) {
        reject(new Error("MQTT connection timeout - will continue retrying in background"));
      }
    }, 10000);

    client.on("connect", () => {
      clearTimeout(timeout);
      console.log("✅ MQTT connected");
      Object.keys(subscriptions).forEach((topic) => {
        client!.subscribe(topic);
        logMqttTraffic('resubscribe', topic);
      });
      resolve(client!);
    });

    client.on("error", (err: Error) => {
      console.error("❌ MQTT error:", err);
      clearTimeout(timeout);
      reject(err);
    });

    client.on("message", (topic: string, message: Buffer) => {
      try {
        const payload: unknown = JSON.parse(message.toString());
        logMqttTraffic('rx', topic, payload);
        Object.entries(subscriptions).forEach(([pattern, callbacks]) => {
          if (topicMatches(pattern, topic)) {
            logMqttTraffic('dispatch', `${topic} -> ${pattern} (${callbacks.length} cb)`);
            callbacks.forEach((cb) => cb(payload));
          }
        });
      } catch (err) {
        console.error("⚠️ MQTT message parse error:", err);
      }
    });
  });
}

// ----------------- Restore MQTT -----------------
export async function restoreMqtt(): Promise<void> {
  const token = (globalState.token as string | null);
  if (!token) return console.warn("⚠️ No token found for MQTT restore") as undefined;
  try {
    await initMqtt(token);
    console.log("🔌 MQTT restored using stored token");
  } catch (err) {
    console.error("❌ MQTT restore failed:", err);
  }
}

// ----------------- Subscribe / Unsubscribe -----------------
export function subscribe(topic: string, callback: MqttMessageCallback): () => void {
  if (!subscriptions[topic]) {
    subscriptions[topic] = [];
    client?.subscribe(topic, (err: Error | null) => {
      if (err) console.error(`❌ MQTT subscribe failed: ${topic}`, err);
      else logMqttTraffic('subscribe', topic);
    });
  } else {
    logMqttTraffic('subscribe-add-callback', `${topic} (${subscriptions[topic].length + 1} cb)`);
  }
  subscriptions[topic].push(callback);
  return () => unsubscribe(topic, callback);
}

export function unsubscribe(topic: string, callback: MqttMessageCallback): void {
  if (!subscriptions[topic]) return;
  subscriptions[topic] = subscriptions[topic].filter((cb) => cb !== callback);
  if (!subscriptions[topic].length) {
    client?.unsubscribe(topic);
    logMqttTraffic('unsubscribe', topic);
    delete subscriptions[topic];
  } else {
    logMqttTraffic('unsubscribe-remove-callback', `${topic} (${subscriptions[topic].length} cb left)`);
  }
}

// ----------------- Convenience Subscribe -----------------
export function subscribeRegister(siteId: string, deviceId: string, registerId: string, callback: MqttMessageCallback): () => void {
  const topic = `sites/${siteId}/devices/${deviceId}/registers/${registerId}`;

  // MQTT stream callback (active only when toggle is ON)
  const mqttWrappedCallback: MqttMessageCallback = (payload: unknown) => {
    if (!isMqttLiveEnabled()) return;
    callback(payload);
  };

  const mqttUnsub = subscribe(topic, mqttWrappedCallback);

  // API polling fallback callback (active only when toggle is OFF)
  const pollingUnsub = addPollingRegisterSubscription(siteId, registerId, callback);

  return () => {
    mqttUnsub();
    pollingUnsub();
  };
}

export function subscribeDeviceState(siteId: string, callback: MqttMessageCallback): () => void {
  const stateTopic = `sites/${siteId}/devices/+/state`;
  const registerTopic = `sites/${siteId}/devices/+/registers/+`;

  // MQTT stream callback (active only when toggle is ON)
  const mqttStateWrappedCallback: MqttMessageCallback = (payload: unknown) => {
    if (!isMqttLiveEnabled()) return;
    if (payload && typeof payload === 'object') {
      callback({ ...(payload as Record<string, unknown>), source: 'mqtt-state' });
      return;
    }
    callback(payload);
  };

  // Register topic adapter: backend commonly publishes per-register topics.
  // Convert each register payload into the same state envelope expected by
  // chart/nav consumers so they can process a unified shape.
  const mqttRegisterWrappedCallback: MqttMessageCallback = (payload: any) => {
    if (!isMqttLiveEnabled()) return;
    if (!payload || typeof payload !== 'object') return;

    const registerId = String(payload?.registerId || '');
    if (!registerId) return;

    const adaptedPayload = {
      siteId,
      deviceId: String(payload?.deviceId || ''),
      source: 'mqtt-register',
      timestamp: payload?.timestamp || new Date().toISOString(),
      readingCount: 1,
      readings: [{
        registerId,
        deviceId: String(payload?.deviceId || ''),
        name: payload?.name,
        address: payload?.address,
        value: payload?.value,
        unit: payload?.unit,
        timestamp: payload?.timestamp || new Date().toISOString(),
        quality: payload?.quality || 'GOOD',
      }],
    };

    callback(adaptedPayload);
  };

  const mqttStateUnsub = subscribe(stateTopic, mqttStateWrappedCallback);
  const mqttRegisterUnsub = subscribe(registerTopic, mqttRegisterWrappedCallback);

  // API polling fallback callback (active only when toggle is OFF)
  const pollingUnsub = addPollingStateSubscription(siteId, callback);

  return () => {
    mqttStateUnsub();
    mqttRegisterUnsub();
    pollingUnsub();
  };
}

export function subscribeSLDStatus(siteId: string, callback: MqttMessageCallback): () => void {
  return subscribe(`scada/${siteId}/status`, callback);
}

// ----------------- Publish / Send Commands -----------------
export function publish(topic: string, message: unknown): void {
  if (!client?.connected) return console.warn("⚠️ MQTT not connected") as undefined;
  client.publish(topic, JSON.stringify(message), (err: Error | undefined) => {
    if (err) console.error(`❌ MQTT publish failed: ${topic}`, err);
  });
}

// Send command to a device register
export function sendCommand(siteId: string, deviceId: string, registerId: string, action: unknown): void {
  // BLOCK IF SITE OR DEVICE IN MAINTENANCE
  const site = globalState.currentSite;
  const device = (globalState.devices as { _id: string; name: string; status: string }[])?.find(d => d._id === deviceId);

  if (site?.status === 'maintenance') {
    return alert("Site is in MAINTENANCE mode. Commands blocked.") as undefined;
  }
  if (device?.status === 'maintenance') {
    return alert(`Device "${device.name}" is in MAINTENANCE mode. Commands blocked.`) as undefined;
  }

  const topic = `scada/${siteId}/control/${deviceId}`;
  const payload = { registerId, action };
  publish(topic, payload);
}

// ----------------- Disconnect -----------------
export function disconnectMqtt(): void {
  if (client) {
    client.end(true);
    client = null;
    console.log("🔌 MQTT disconnected");
  }
}
